import React from 'react';
import { motion } from 'framer-motion';
import { Check, FileCode } from 'lucide-react';
import { formatElapsed } from '@/lib/generationProgress';

// The stages, in the order the generator writes them. Each one lights up as the
// stream reaches it and stays lit, so the whole shape of the job is visible from
// the first second rather than only the step currently running.
const STEPS = [
  { key: 'plan', label: 'Plan' },
  { key: 'pages', label: 'Pages' },
  { key: 'css', label: 'Style' },
  { key: 'js', label: 'Interactions' },
];

const STEP_ORDER = ['plan', 'pages', 'css', 'js', 'done'];

/**
 * The always-visible progress strip for a running generation.
 *
 * It sits under the workspace top bar rather than inside the preview, because
 * the preview stops being empty about ten seconds in — the site starts drawing
 * itself while most of the work (the remaining pages, the stylesheet, the
 * scripts) is still to come, and that is exactly the stretch the wait used to
 * feel dead.
 *
 * @param {object} props
 * @param {ReturnType<import('@/hooks/useGenerationProgress').useGenerationProgress>} props.progress
 */
export default function GenerationProgressBar({ progress }) {
  const { percent, phase, detail, stepKey, filesDone, elapsedMs, mode } = progress;
  const reached = STEP_ORDER.indexOf(stepKey);
  // A patched edit writes no plan, pages, style or script in turn; pips for
  // them would sit unlit until it finished and then all light at once.
  const showSteps = mode !== 'edit';
  // Newest first: the last few files written are the interesting ones.
  const recentFiles = filesDone.slice(-3).reverse();

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.25 }}
      className="shrink-0 border-b border-white/5 bg-zinc-950/90 overflow-hidden"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${mode === 'edit' || mode === 'rewrite' ? 'Updating' : 'Building'} your website — ${phase}`}
    >
      <div className="flex items-center gap-3 px-4 pt-2 pb-1.5">
        {/* Current phase */}
        <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse shadow-[0_0_10px_rgba(249,115,22,0.8)] shrink-0" />
        <div className="min-w-0 max-w-[280px]">
          <p className="text-xs font-medium text-white truncate leading-tight">{phase}</p>
          <p className="text-[11px] text-zinc-500 truncate leading-tight">{detail}</p>
        </div>

        {/* Stage pips */}
        <div className="hidden md:flex items-center gap-1.5 shrink-0 ml-1">
          {showSteps && STEPS.map((step, i) => {
            const at = STEP_ORDER.indexOf(step.key);
            const done = reached > at;
            const active = reached === at;
            return (
              <React.Fragment key={step.key}>
                {i > 0 && <span className={`w-3 h-px ${done || active ? 'bg-zinc-700' : 'bg-zinc-800'}`} />}
                <span
                  className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border transition-colors ${
                    done
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                      : active
                        ? 'border-orange-500/40 bg-orange-500/10 text-orange-300'
                        : 'border-zinc-800 text-zinc-600'
                  }`}
                >
                  {done && <Check className="w-2.5 h-2.5" />}
                  {step.label}
                </span>
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex-1" />

        {/* Files already written */}
        <div className="hidden lg:flex items-center gap-1.5 shrink-0">
          {recentFiles.map(file => (
            <span
              key={file}
              className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-full px-2 py-0.5 text-[10px] text-zinc-400"
            >
              <FileCode className="w-2.5 h-2.5 text-orange-400" />
              {file}
            </span>
          ))}
        </div>

        {/* Numbers */}
        <div className="flex items-baseline gap-2 shrink-0">
          <span className="text-sm font-semibold text-orange-400 tabular-nums">{percent}%</span>
          <span className="text-[11px] text-zinc-500 tabular-nums w-8 text-right">{formatElapsed(elapsedMs)}</span>
        </div>
      </div>

      {/* The bar itself. Width transitions rather than animating per chunk —
          chunks land many times a second and each one re-renders this. */}
      <div className="h-1 bg-zinc-900 relative overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-[width] duration-700 ease-out relative"
          style={{ width: `${percent}%` }}
        >
          <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent animate-[shimmer_1.8s_infinite]" />
        </div>
      </div>
    </motion.div>
  );
}
